'use client';
import dynamic from 'next/dynamic';
import { Box } from '@mui/material';
import React, {
    createRef,
    ReactNode,
    RefObject,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { numberWithCommas } from '@/utils';
import styles from './styles.module.scss';
import { useAnalytics } from '@/integrations';
import { useTranslation, useWindowDimensions } from '@/hook';
import {
    get3dImperfections,
    setImperfectionsImages,
    setImperfectionsImagesCategories,
} from '@/redux/reducers/gallery.reducer';
import { useAppSelector } from '@/hook/redux-hooks';
import {
    StyledTab,
    StyledTabs,
} from '@/components/shared/TabsThemed/tabs-themed.component';
import CarDisplayCardClient from '@/components/shared/car-details-card/car-details-car-client.component';
import { FlapperLine } from '@/components/shared/car-details-card-v2/flapper-line.component';
import Typography from '@/components/shared/typography/typography.component';
import {
    carFrame,
    ComfortConvenienceIconImg,
    DimensionsCapacityIconImg,
    engine,
    EntertainmentCommunicationIconImg,
    ExteriorIconImg,
    FaultyCheckMark,
    FixedMark,
    HighlightsIcon,
    imgs,
    ImperfectionCheckMark,
    InteriorIconImg,
    PerformanceFeatureIconImg,
    SafetyIconImg,
    SylndrCertifiedIcon,
    EntryPointCarDetails,
    ChevronRightWhite,
} from '../../../public/Images';
import Image from 'next/image';
import {
    AppDispatch,
    CarDetailsType,
    GetCarListing,
    ImperfectionReportItem,
    ImperfectionReportSection,
    ImperfectionReportSectionGrouped,
    ImperfectionStatusEnum,
    IQCReportUIProperty,
    LenderWithInterestRates,
} from '@/types';
import { useDispatch } from 'react-redux';
import { ModalTabsText } from './CarProfileNew/StyledComponents/modal-components';
import { CarInfo, GridSection } from './CarProfileNew';
import { EGGRID_ITEM_VARIANT } from './CarProfileNew/GridSection/car-grid.component';
import { formatDate, getCarCardLocale, openApp } from '@/utils';
import { CarProfileContext } from '@/contexts/carProfile.context';
import CarSoldInfoBoxMobile from './CarProfileNew/SimilarCars/CarSoldInfoBoxMobile';
import { CarListingData } from '@/types/cars.types';
import {
    AVAILABLE_LANGUAGES,
    FEATURE_FLAG_APP_ENTRY_CAR_DETAILS,
    ServiceName,
    SINGULAR_UNI_LINK_CAR_PROFILE,
} from '@/constants';
import { DimensionsTargets } from '@/hook/use-window-width.hook';
import BlinkingSkeleton from '../shared/blinking-skeleton/blinking-skeleton.component';
import ReserveInfoBox from './ReserveInfoBox/reserve-info-box.component';
import VidTestimonialsSection from '@/components/testimonialVidSection/vid-testimonials.component';
import { SourceScreen } from '@/types/source-screens.types';

import { PurchaseCarSteps } from '@/components/purchaseCarSteps';

import { CarGuarantee } from './CarProfileNew/CarInfoSection/car-info.component';
import Button from '../shared/Button/button.component';
import useAuth from '@/hook/use-auth.hook';
import myImageLoader from '@/image.local.loader';
import { useInView } from 'react-intersection-observer';
import CarFeaturesSection from './car-features/car-features';
import { carDetailsService } from '@/api-requests';
import { useParams } from 'next/navigation';
import MobileButtons from './mobile-buttons';
import FinancingValuePropositions from '@/components/car-finance/value-propositions';
import FinanceCalculatorNew from '@/components/shared/FinanceCalculatorNew';
import ProfileSkeleton from './CarProfileNew/Gallery/gallerySkeleton';
import { setSelectedCar } from '@/redux/reducers/checkoutReducer';
import ValuePropositionSlider from '../shared/value-proposition-slider/value-proposition-slider.component';
import { getCategoryBadgeConfig } from './CarProfileNew/constants';

const SimilarCarsContainer = dynamic(
    () =>
        import('./CarProfileNew/SimilarCars/SimilarCarsContainer').then(
            mod => mod.default
        ),
    {
        ssr: false,
    }
);
const ImperfectionOverviewSectioned = dynamic(
    () =>
        import(
            './CarProfileNew/ImperfectionSection/imperfection-overview-sectioned.component'
        ).then(mod => mod.default),
    { ssr: false }
);
const GrouppedImprefectionReportModal = dynamic(
    () =>
        import(
            './CarProfileNew/ImprefectionReportGroupped/imprefection-report-modal.component'
        ).then(mod => mod.default),
    { ssr: false }
);
const GrouppedImprefectionReportBottomSheet = dynamic(
    () =>
        import(
            './CarProfileNew/ImprefectionReportGroupped/imprefection-report-bottomsheet.component'
        ).then(mod => mod.default),
    { ssr: false }
);

const Gallery = dynamic(
    () => import('./CarProfileNew/Gallery').then(mod => mod.Gallery),
    {
        ssr: false,
        loading: () => <ProfileSkeleton />,
    }
);

export default function CarDetailsComponent({
    lang,
    carDetails,
    lenders,
}: {
    lang: AVAILABLE_LANGUAGES;
    carDetails: CarDetailsType;
    lenders: LenderWithInterestRates[];
}) {
    const { carId } = useParams();
    const [imperfectionDataGroupped, setImperfectionDataGroupped] =
        useState<ImperfectionReportSectionGrouped | null>(null);
    const [similarCars, setSimilarCars] = useState<GetCarListing | null>(null);
    useEffect(() => {
        carDetailsService
            .getImperfectionReportGroupped({
                carId: carId as string,
                lang,
            })
            .then(data => {
                setImperfectionDataGroupped(data.data);
            });
        carDetailsService
            .getSimilarCars({
                carId: carId as string,
                lang,
            })
            .then(data => {
                setSimilarCars(data);
            });
    }, []);

    const { t, IS_RTL } = useTranslation();
    const [value, setValue] = useState(0);
    const [shouldRenderModals, setShouldRenderModals] = useState(false);
    const { ref: videoSectionRef, inView: videoSectionInView } = useInView({
        triggerOnce: true,
        rootMargin: '1500px',
    });
    const { ref: similarCarsSectionRef, inView: similarCarsSectionInView } =
        useInView({
            triggerOnce: true,
            rootMargin: '1500px',
        });
    const { ref: featuredGridRef, inView: featuredGridInView } = useInView({
        root: null,
        triggerOnce: true,
        rootMargin: '1500px',
    });
    const { ref: valuePropRef, inView: valuePropInView } = useInView({
        root: null,
        triggerOnce: true,
        rootMargin: '1500px',
    });
    const { ref: imperfectionSectionRef, inView: imperfectionSectionInView } =
        useInView({
            root: null,
            triggerOnce: true,
            rootMargin: '1500px',
        });
    const { ref: carOverviewSectionRef, inView: carOverviewSectionInView } =
        useInView({
            root: null,
            triggerOnce: true,
            rootMargin: '1500px',
        });
    const { ref: purchaseStepsSectionRef, inView: purchaseStepsSectionInView } =
        useInView({
            root: null,
            triggerOnce: true,
            rootMargin: '1500px',
        });

    useEffect(() => {
        setTimeout(() => {
            setShouldRenderModals(true);
        }, 1000);
    }, []);
    const dispatch = useDispatch<AppDispatch>();
    const showAppEntryPoint = FEATURE_FLAG_APP_ENTRY_CAR_DETAILS;
    const { IS_DESKTOP } = useWindowDimensions(DimensionsTargets.MOBILE);
    const [framesWidth, setFramesWidth] = useState(500);
    const [framesHeight, setFramesHeight] = useState(400);
    useEffect(() => {
        if (IS_DESKTOP && framesWidth !== 800) {
            setFramesWidth(800);
            setFramesHeight(800);
        } else if (!IS_DESKTOP && framesWidth !== 500) {
            setFramesWidth(500);
            setFramesHeight(400);
        }
    }, [IS_DESKTOP]);

    useEffect(() => {
        if (!carDetails) {
            return;
        }
        trackEvent('Screen: Car Profile', {
            carName: carDetails.sfVehicleName,
            make: carDetails.make,
            model: carDetails.model,
            year: carDetails.year,
            trim: carDetails.trim?.en,
            kilometers: carDetails.kilometers,
            price: carDetails.sylndrSellingPrice,
            status: carDetails.isPurchaseInProgress
                ? 'Being Sold'
                : carDetails.isReserved
                ? 'Reserved'
                : 'Not Reserved',
            monthlyInstallment: carDetails.installmentPrice,
            transmission: carDetails.transmission,
            //@TODO :: neeeds to be added. check the history of the file.
            // rTyres: rTyres?.faultyPoints ?? 0,
            // rExterior: rExterior?.faultyPoints ?? 0,
            // rInterior: rInterior?.faultyPoints ?? 0,
            // rMechanical: rMechanical?.faultyPoints ?? 0,
            // rElectricals: rElectricals?.faultyPoints ?? 0,
            // rSuspension: rSuspension?.faultyPoints ?? 0,
            newlyAdded:
                carDetails.isNewlyAdded &&
                !(carDetails.isReserved || carDetails.isPurchaseInProgress),
            carLink: `${window.location.origin}/${lang}/car-details/${carDetails.id}`,
            isRefurbished: carDetails.isRefurbished,
        });
    }, [carDetails]);

    const gallery = {
        width: '100%',
        marginBottom: IS_DESKTOP ? '0' : '2vw',
    };
    const carOverview = {
        width: '100%',
        marginBottom: '2vw',
    };
    const sectionHeader = {
        color: '#002D5D',
        opacity: 1,
        marginBottom: '8px',
    };

    const carFeature = {
        width: '100%',
        marginBottom: '2vw',
    };

    const carInspection = {
        width: '100%',
        marginBottom: '2vw',
    };

    const whySylndrContainer = {
        marginBottom: '2vw',
    };

    ////////////////////////////////////////////////
    const stickyContainer = {
        position: IS_DESKTOP ? 'sticky' : '',
        top: '20px',
        width: '100%',
        height: 'fit-content',
    };

    const similarCarsContainer = {
        marginTop: '32px',
        position: 'relative',
        borderTop: 'solid 1px #EAECF0',
    };
    const similarCarsText = {
        color: '#101828',
        position: 'absolute',
        top: 0,
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 1,
        backgroundColor: 'white',
        padding: '0px 8px',
    };

    const [openFeatureContainer, setOpenFeatureContainer] = useState(false);
    const [featureValue, setFeatureValue] = useState(0);

    const [initialOpenedTab, setInitialOpenedTab] = useState(0);
    const [userInterActed, setUserInterActed] = useState(false);
    const [scrollToPosition, setScrollToPosition] = useState<number>(0);
    const { trackEvent } = useAnalytics();
    const { userId, phone, email } = useAuth();

    const fireEvent = () => {
        if (!userInterActed) {
            setUserInterActed(true);
            trackEvent('Action: Gallery', {
                interaction: true,
                carName: carDetails?.sfVehicleName,
                isRefurbished: carDetails?.isRefurbished,
            });
        }
    };
    const [opacity, setOpacity] = useState<number>(1);
    const [openGrouppedImprefectionModal, setOpenGrouppedImprefectionModal] =
        useState(false);
    const closeGrouppedImprefectionModalHandler = () => {
        setOpenGrouppedImprefectionModal(false);
    };
    const debounce = (func: any, delay: number) => {
        let timeout;
        return (...args) => {
            clearTimeout(timeout);
            timeout = setTimeout(() => func(...args), delay);
        };
    };

    const listenScrollEvent = debounce(() => {
        if (!similarCarsStaticRef) return;
        const y = similarCarsStaticRef?.current?.offsetTop || 0;
        const diff: number = y - 700 - window.scrollY;
        const shouldBeginTransition: boolean = diff < 100 && diff >= 0;
        const shouldEndTransition: boolean = diff <= 0;
        setScrollToPosition(similarCarsStaticRef?.current?.offsetTop || 0);
        if (shouldEndTransition) {
            return setOpacity(0);
        } else if (shouldBeginTransition) {
            setOpacity(diff / 100);
        } else {
            setOpacity(1);
        }
    }, 300);
    //////////////////////////////////////////////////////////////////////
    const [mobileTabs, setMobilesTabs] = useState<string[]>([]);
    const tabsRef = useRef<RefObject<HTMLDivElement>[]>(
        Array.from({ length: 4 as number }, () => createRef())
    );

    const mainRef = useRef<HTMLDivElement>(null);
    const galleryRef = useRef<HTMLDivElement>(null);
    const infoRef = useRef<HTMLDivElement>(null);
    const similarCarsStaticRef = useRef<HTMLDivElement>(null);
    const carDetailsTableRef = useRef<HTMLDivElement>(null);

    const getDimensions = (ele: HTMLDivElement) => {
        const { height } = ele.getBoundingClientRect();
        const offsetTop = ele.offsetTop;
        const offsetBottom = offsetTop + height;
        return {
            height,
            offsetTop,
            offsetBottom,
        };
    };

    const handleScroll = () => {
        if (!mainRef?.current || !galleryRef?.current || !infoRef?.current)
            return;
        const { height: galleryHeight } =
            galleryRef.current.getBoundingClientRect();
        const { height: infoHeight } = infoRef.current.getBoundingClientRect();
        const UPPER_MARGIN_HEIGHT = IS_DESKTOP
            ? galleryHeight
            : galleryHeight + infoHeight + 60;
        const scrollPosition = window.scrollY - UPPER_MARGIN_HEIGHT + 24;

        tabsRef?.current?.forEach((ref, index) => {
            const ele = ref.current;

            if (ele) {
                const { offsetBottom, offsetTop } = getDimensions(ele);

                if (
                    scrollPosition > offsetTop &&
                    scrollPosition < offsetBottom
                ) {
                    setValue(index);
                }
            }
        });
    };
    const handleInitialTabsLogic = () => {
        let tabs = [];
        tabs = [
            t('carGuarantee'),
            t('carSummary'),
            t('Overview'),
            t('inspection'),
            t('Features'),
            t('Financing'),
        ];
        if (!IS_DESKTOP) {
            tabs.splice(0, 2);
            if (!IS_DESKTOP) {
                const overviewIndex = tabs.indexOf(t('Overview'));
                if (overviewIndex !== -1) {
                    tabs.splice(overviewIndex, 1);
                }
            }
        } else {
            if (tabs[0] != t('carGuarantee')) {
                tabs.splice(0, 0, t('carGuarantee'));
            }
            if (tabs[1] != t('carSummary')) {
                tabs.splice(1, 0, t('carSummary'));
            }
        }

        setMobilesTabs(tabs);
        tabsRef.current = Array.from({ length: tabs.length as number }, () =>
            createRef()
        );
    };
    const getSectionRefIndex = (key: string) => {
        return mobileTabs.findIndex(item => item === key);
    };
    const carType = carDetails.category;

    const badgeConfig = useMemo(
        () => getCategoryBadgeConfig(carType, t),
        [carType, t]
    );

    const carDetailsOverview = useMemo(() => {
        return [
            <span
                key="refurbished-badge"
                className="flex flex-row gap-1"
                style={{
                    backgroundColor: badgeConfig.bgColor,
                    padding: '2px 8px',
                    borderRadius: '4px',
                }}
                onClick={() => {
                    const elementHeight = 600;
                    const scrolledY = window.scrollY;
                    if (!scrolledY) {
                        window.scrollTo(0, elementHeight + scrolledY);
                    }
                    if (scrolledY) {
                        window.scrollTo(0, scrolledY - elementHeight);
                    }
                }}
            >
                <img
                    src={myImageLoader({
                        src: badgeConfig.icon,
                        width: 20,
                        height: 20,
                    })}
                    alt={`Sylndr ${badgeConfig.label}`}
                />
                <Typography variant="captionBold" color={badgeConfig.textColor}>
                    {badgeConfig.label}
                </Typography>
            </span>,
            t('kilometersWithValue', {
                value: numberWithCommas(carDetails?.kilometers),
            }),
            t('engineCapacityWithValue', {
                value: carDetails?.engineCapacity,
            }),
            t(carDetails?.color),
            t(carDetails?.transmission),
            t('licenseValidToWithValue', {
                value: formatDate(carDetails?.liscenseValidityDate),
                interpolation: {
                    escapeValue: false,
                },
            }),
        ];
    }, [IS_RTL, carDetails]);

    const carInfo = useMemo(() => {
        return {
            id: carDetails?.id,
            make: t(carDetails?.make),
            model: carDetails?.model,
            year: carDetails?.year,
            trim: carDetails?.trim,
            kilometers: carDetails?.kilometers,
            transmission:
                carDetails?.transmission === 'automatic'
                    ? t('automatic')
                    : t('manual'),
            sylndrSellingPrice: carDetails?.sylndrSellingPrice,
            discountPrice: carDetails?.discountPrice,
            installmentPrice: carDetails?.installmentPrice,
            isReserved: carDetails?.isReserved,
            carName: carDetails?.sfVehicleName,
            isSold: carDetails?.isSold,
            isPublished: carDetails?.isPublished,
            isPurchaseInProgress: carDetails?.isPurchaseInProgress,
            showroom: carDetails?.showroom,
            registrationFees: carDetails?.registrationFees,
            bookingType: carDetails?.bookingType,
            isNewlyAdded: carDetails?.isNewlyAdded,
            isRefurbished: carDetails?.isRefurbished,
        };
    }, [carDetails]);

    const carOverviewState = useMemo(() => {
        return {
            make: t(carDetails?.make),
            model: carDetails?.model,
            year: carDetails?.year,
            Trim: IS_RTL ? carDetails?.trim?.ar : carDetails?.trim?.en,
            Color: t(carDetails?.color),
            kilometers: numberWithCommas(carDetails?.kilometers),
            Transmission:
                carDetails?.transmission === 'automatic'
                    ? t('automatic')
                    : t('manual'),
            'Body Style': t(carDetails?.bodyStyle),
            'Engine Capacity CC': carDetails?.engineCapacity,
            liscenseValidityDate: formatDate(carDetails?.liscenseValidityDate),
        };
    }, [carDetails]);

    const carPrice = useMemo(() => {
        const hasDiscount =
            carDetails?.discountPrice !== undefined &&
            carDetails?.discountPrice !== 0;
        return hasDiscount
            ? carDetails?.discountPrice
            : parseFloat(carDetails?.sylndrSellingPrice as string);
    }, [carDetails]);

    useEffect(() => {
        dispatch(get3dImperfections(carDetails.id));
        dispatch(setSelectedCar(carDetails));
        handleInitialTabsLogic();

        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
        window.addEventListener('scroll', handleScroll);
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [carDetails, IS_DESKTOP]);

    useEffect(() => {
        window.addEventListener('scroll', listenScrollEvent);
        setTimeout(() => {
            window.scrollTo({ top: 1 });
        }, 0);
        setScrollToPosition(similarCarsStaticRef?.current?.offsetTop || 0);
        return () => {
            window.removeEventListener('scroll', listenScrollEvent);
        };
    }, []);

    const imprefections3d = useAppSelector(
        state => state.gallery.imperfections3d ?? []
    );

    const getIconFromStatus = (status: ImperfectionStatusEnum): ReactNode => {
        switch (status) {
            case ImperfectionStatusEnum.Faulty:
                return (
                    <Image
                        src={FaultyCheckMark}
                        width="20"
                        height="20"
                        alt="Faulty"
                    />
                );
            case ImperfectionStatusEnum.UnFaulty:
                return (
                    <Image
                        src={ImperfectionCheckMark}
                        width="20"
                        height="20"
                        alt="Good"
                    />
                );
            case ImperfectionStatusEnum.Fixed:
                return (
                    <Image src={FixedMark} width="20" height="20" alt="Fixed" />
                );
            default:
                return null;
        }
    };

    const intiateImperfectionsReportData = (
        ImpReport: ImperfectionReportSectionGrouped
    ) => {
        const imperfectionsGalleryCategories: any[] = [];
        const imperfectionsGallery: any[] = [];

        const processSection = (section: ImperfectionReportSection) => {
            const properties: IQCReportUIProperty[] = [];
            section.items.forEach((reportItem: ImperfectionReportItem) => {
                const propertyObj = {
                    id: reportItem.imperfection_item_id,
                    name: IS_RTL ? reportItem.name_ar : reportItem.name_en,
                    status: reportItem.status as ImperfectionStatusEnum,
                    value: getIconFromStatus(reportItem.status),
                    comment: IS_RTL
                        ? reportItem.comment_ar
                        : reportItem.comment_en,
                    tags: reportItem?.tags?.map(tag =>
                        IS_RTL ? tag.tag_ar : tag.tag_en
                    ),
                    images: reportItem.images,
                };

                if (reportItem.images && reportItem.images.length > 0) {
                    const categoryObj = {
                        name: IS_RTL ? section.name_ar : section.name_en,
                        icon: (
                            <Image
                                width={36}
                                height={36}
                                style={{ objectFit: 'contain' }}
                                src={section?.section_good_icon}
                                alt={section.name_en}
                            />
                        ),
                    };

                    let categoryIndex =
                        imperfectionsGalleryCategories.findIndex(
                            category => category.name === categoryObj.name
                        );

                    if (categoryIndex === -1) {
                        categoryIndex = imperfectionsGalleryCategories.length;
                        imperfectionsGalleryCategories.push(categoryObj);
                    }

                    const toBeAddedImages = reportItem.images.map(image => ({
                        newGallery: carDetails?.newGallery,
                        original: image,
                        thumbnail: image,
                        name: IS_RTL ? reportItem.name_ar : reportItem.name_en,
                        comment: IS_RTL
                            ? reportItem.comment_ar
                            : reportItem.comment_en,
                        tags: reportItem?.tags?.map(tag =>
                            IS_RTL ? tag.tag_ar : tag.tag_en
                        ),
                        category: categoryIndex,
                        sectionImg: reportItem?.highlightedImages?.[0] ?? null,
                    }));

                    imperfectionsGallery.push(...toBeAddedImages);
                }

                properties.push(propertyObj);
            });

            return properties;
        };

        if (ImpReport.exterior) {
            ImpReport.exterior.forEach(section => processSection(section));
        }

        if (ImpReport.faulty) {
            ImpReport.faulty.forEach(section => processSection(section));
        }

        dispatch(
            setImperfectionsImagesCategories(imperfectionsGalleryCategories)
        );
        dispatch(setImperfectionsImages(imperfectionsGallery));
    };
    useEffect(() => {
        if (imperfectionDataGroupped) {
            intiateImperfectionsReportData(imperfectionDataGroupped);
        }
    }, [imperfectionDataGroupped]);

    const grouppedImperfectionItemClicked = (index: number) => {
        setInitialOpenedTab(index);
        setOpenGrouppedImprefectionModal(true);
    };
    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
        if (!galleryRef?.current || !infoRef?.current) return;
        const { height: galleryHeight } =
            galleryRef.current.getBoundingClientRect();
        const { height: infoHeight } = infoRef.current.getBoundingClientRect();
        const { offsetTop } = getDimensions(tabsRef.current[newValue].current);
        const UPPER_MARGIN_HEIGHT = IS_DESKTOP
            ? galleryHeight
            : galleryHeight + infoHeight + 60;
        window.scrollTo({
            top: offsetTop + UPPER_MARGIN_HEIGHT + 24,
            left: 0,
            behavior: 'smooth',
        });
    };
    const carFeaturesObject = {
        'Performance Features': PerformanceFeatureIconImg,
        'Comfort & Convenience': ComfortConvenienceIconImg,
        'Entertainment & Communication': EntertainmentCommunicationIconImg,
        Interiors: InteriorIconImg,
        Safety: SafetyIconImg,
        Exteriors: ExteriorIconImg,
        'Dimensions & Capacity': DimensionsCapacityIconImg,
        'Sylndr certified': SylndrCertifiedIcon,
    };

    const featureitemClicked = (id: string, isViewAll = false) => {
        const featureKeys = Object.keys(carFeaturesObject);
        const target = featureKeys.indexOf(id);
        const index = target === featureKeys.length - 1 ? 0 : target;
        trackEvent('Action: Features', {
            sourceButton: isViewAll ? 'View All Features' : featureKeys[target],
            carName: carDetails?.sfVehicleName,
            isRefurbished: carDetails?.isRefurbished,
        });

        setFeatureValue(index);
        setOpenFeatureContainer(true);
    };

    const carCardLocalizations = getCarCardLocale();
    const discountPrice = carDetails.discountPrice;
    const hasDiscount = discountPrice !== undefined && discountPrice !== 0;
    return (
        <div
            className={`${
                openGrouppedImprefectionModal && '!overflow-y-hidden '
            }`}
        >
            <CarProfileContext.Provider
                value={{
                    scrollToPosition,
                    setScrollToPosition,
                }}
            >
                <div className=" md:px-[5%] px-4 py-[3vw]">
                    <div className={styles.containerInner}>
                        {(carDetails?.isSold ||
                            carDetails?.isPurchaseInProgress) &&
                            !IS_DESKTOP && (
                                <CarSoldInfoBoxMobile
                                    type={
                                        carDetails?.isPurchaseInProgress
                                            ? 'PURCHASE_IN_PROGRESS'
                                            : 'SOLD'
                                    }
                                />
                            )}
                        {carDetails.isReserved &&
                            !carDetails.isPurchaseInProgress && (
                                <ReserveInfoBox />
                            )}

                        <div className={styles.layout}>
                            <div
                                ref={galleryRef}
                                style={{ ...gallery, gridArea: 'img' }}
                            >
                                {carDetails !== undefined ? (
                                    carDetails.newGallery &&
                                    carDetails.gallery.gallery && (
                                        <Gallery
                                            shouldRenderModals={
                                                shouldRenderModals
                                            }
                                            buckets={carDetails.gallery.gallery}
                                            images={(
                                                carDetails?.frames ||
                                                carDetails?.images ||
                                                []
                                            ).map(imgUrl =>
                                                myImageLoader({
                                                    src: imgUrl,
                                                    width: framesWidth,
                                                    height: framesHeight,
                                                    quality: 95,
                                                })
                                            )}
                                            imperfections={imprefections3d}
                                            fireEvent={fireEvent}
                                            interior3d={carDetails?.interior3d}
                                            className={
                                                'max-md:-mx-[5%] max-md:w-svw'
                                            }
                                            altImageText={
                                                carDetails?.make +
                                                ' ' +
                                                carDetails?.model +
                                                ' ' +
                                                carDetails?.year
                                            }
                                        />
                                    )
                                ) : (
                                    <BlinkingSkeleton
                                        height={IS_DESKTOP ? '50vw' : '40vw'}
                                        minHeight={IS_DESKTOP ? '45vw' : '50vh'}
                                    />
                                )}

                                <FlapperLine
                                    className={
                                        'max-md:-mx-[5%] max-md:w-svw bg-gray-50 md:hidden'
                                    }
                                    hasDiscount={hasDiscount}
                                    discountText={t('limitedOffer')}
                                    tags={carDetails.tags}
                                    lang={lang as AVAILABLE_LANGUAGES}
                                />
                            </div>

                            <div
                                style={{
                                    position: 'relative',
                                    gridArea: 'dt',
                                }}
                                ref={mainRef}
                            >
                                <div className="sticky top-[-1] bg-white z-[2] mb-6 md:min-h-16">
                                    <StyledTabs
                                        // className={classes.root}
                                        value={value}
                                        onChange={handleChange}
                                        variant="scrollable"
                                        scrollButtons={false}
                                    >
                                        {mobileTabs?.map(item => {
                                            return (
                                                <StyledTab
                                                    label={
                                                        <ModalTabsText variant="body2Medium">
                                                            {item}
                                                        </ModalTabsText>
                                                    }
                                                    key={item}
                                                />
                                            );
                                        })}
                                    </StyledTabs>
                                </div>

                                <div
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(
                                                t('carGuarantee')
                                            )
                                        ]
                                    }
                                    className="hidden md:block w-full  mb-[2vw]"
                                >
                                    <CarGuarantee
                                        carDetailsTableRef={carDetailsTableRef}
                                    />
                                </div>
                                <div
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('carSummary'))
                                        ]
                                    }
                                    className="hidden md:block w-full  mb-[2vw]"
                                >
                                    <div className="text-[#002d5d] text-xl font-bold leading-[30px] mb-2">
                                        {t('carSummary')}
                                    </div>
                                    <div className="border flex flex-wrap border-solid border-gray-100 rounded-lg py-6 px-4 gap-5">
                                        <div className="flex  gap-x-2 p-x-1 border-r border-solid border-gray-100 w-[calc(33.333%-1.25rem)] items-center last:border-0">
                                            <Image
                                                className="w-5 h-5"
                                                src={carFrame}
                                                alt="frame"
                                            />
                                            {imperfectionDataGroupped?.isRepainted ? (
                                                imperfectionDataGroupped?.numberOfRepaintedPanels ==
                                                1 ? (
                                                    <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                                        {
                                                            imperfectionDataGroupped?.numberOfRepaintedPanels
                                                        }{' '}
                                                        {t('repaintedPart')}
                                                    </div>
                                                ) : imperfectionDataGroupped?.numberOfRepaintedPanels >
                                                  3 ? (
                                                    <div className="text-gray-600 text-sm font-normal leading-tight">
                                                        {t('fewRepaintedParts')}
                                                    </div>
                                                ) : (
                                                    <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                                        {
                                                            imperfectionDataGroupped?.numberOfRepaintedPanels
                                                        }{' '}
                                                        {t('repaintedParts')}
                                                    </div>
                                                )
                                            ) : (
                                                <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                                    {t('noRepaintedParts')}
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex  gap-x-2 p-x-1 border-r border-solid border-gray-100 w-[calc(33.333%-1.25rem)] items-center last:border-0 ">
                                            <Image
                                                className="w-5 h-5"
                                                src={engine}
                                                alt="engine"
                                            />

                                            <div className="text-gray-600 text-sm font-normal leading-tight ar:mt-1">
                                                {t('noMechProblems')}
                                            </div>
                                        </div>

                                        {carDetails?.highlights?.ar.map(
                                            (h, index) => (
                                                <div
                                                    className={`flex  gap-x-2 px-1 ${
                                                        index % 3 == 0
                                                            ? ''
                                                            : 'border-r border-solid border-gray-100'
                                                    } w-[calc(33.333%-1.25rem)] items-center last:border-0 `}
                                                    key={`${h} highlight-item`}
                                                >
                                                    <Image
                                                        className="w-5 h-5"
                                                        src={HighlightsIcon}
                                                        alt="star"
                                                    />
                                                    <div className="en:hidden text-gray-600 text-sm font-normal leading-tight mt-1">
                                                        {
                                                            carDetails
                                                                .highlights?.ar[
                                                                index
                                                            ]
                                                        }
                                                    </div>
                                                    <div className="ar:hidden text-gray-600 text-sm font-normal leading-tight">
                                                        {
                                                            carDetails
                                                                .highlights?.en[
                                                                index
                                                            ]
                                                        }
                                                    </div>
                                                </div>
                                            )
                                        )}
                                    </div>
                                </div>
                                <Box
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('Overview'))
                                        ]
                                    }
                                    sx={{ ...carOverview, gridArea: 'ov' }}
                                >
                                    <div ref={carOverviewSectionRef}>
                                        {carOverviewSectionInView &&
                                            IS_DESKTOP &&
                                            carOverviewState && (
                                                <GridSection
                                                    gridItemVariant={
                                                        EGGRID_ITEM_VARIANT.TEXT
                                                    }
                                                    sectionHeader={t(
                                                        'Car Overview'
                                                    )}
                                                    gridItems={carOverviewState}
                                                />
                                            )}
                                    </div>
                                </Box>
                                {/* groupped imperfection report */}
                                <Box
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('inspection'))
                                        ]
                                    }
                                    sx={{
                                        ...carInspection,
                                        gridArea: 'rp',
                                    }}
                                >
                                    <div ref={imperfectionSectionRef}>
                                        {(imperfectionSectionInView ||
                                            IS_DESKTOP) &&
                                            imperfectionDataGroupped && (
                                                <ImperfectionOverviewSectioned
                                                    t={t}
                                                    sectionHeader={t(
                                                        'imperfectionReport'
                                                    )}
                                                    itemClicked={
                                                        grouppedImperfectionItemClicked
                                                    }
                                                    imperfectionReportUnfaultyItems={
                                                        imperfectionDataGroupped.unFaulty
                                                    }
                                                    lang={lang}
                                                    isRepainted={
                                                        imperfectionDataGroupped.isRepainted
                                                    }
                                                    eventData={{
                                                        carName:
                                                            carDetails?.sfVehicleName,
                                                        totalRepaintedParts:
                                                            imperfectionDataGroupped.numberOfRepaintedPanels,
                                                        totalImperfections:
                                                            imperfectionDataGroupped.faulty.reduce(
                                                                (acc, obj) =>
                                                                    acc +
                                                                    obj.items
                                                                        .length,
                                                                0
                                                            ),
                                                    }}
                                                />
                                            )}
                                    </div>

                                    {shouldRenderModals &&
                                        carDetails &&
                                        imperfectionDataGroupped &&
                                        (IS_DESKTOP ? (
                                            <GrouppedImprefectionReportModal
                                                open={
                                                    openGrouppedImprefectionModal
                                                }
                                                setOpen={
                                                    closeGrouppedImprefectionModalHandler
                                                }
                                                carDisplayName={`${t(
                                                    carDetails?.make
                                                )} ${carDetails?.model} ${
                                                    carDetails.year
                                                }`}
                                                carImage={carDetails?.images[0]}
                                                imperfectionDataGroupped={
                                                    imperfectionDataGroupped
                                                }
                                                initialActiveTab={
                                                    initialOpenedTab
                                                }
                                                highlights={
                                                    carDetails?.highlights
                                                }
                                                IS_RTL={IS_RTL}
                                                t={t}
                                            />
                                        ) : (
                                            <GrouppedImprefectionReportBottomSheet
                                                carInfo={carInfo}
                                                carDetails={carDetails}
                                                lang={lang}
                                                open={
                                                    openGrouppedImprefectionModal
                                                }
                                                setOpen={
                                                    closeGrouppedImprefectionModalHandler
                                                }
                                                carDisplayName={`${t(
                                                    carDetails?.make
                                                )} ${carDetails?.model} ${
                                                    carDetails.year
                                                }`}
                                                carImage={carDetails?.images[0]}
                                                imperfectionDataGroupped={
                                                    imperfectionDataGroupped
                                                }
                                                initialActiveTab={
                                                    initialOpenedTab
                                                }
                                                highlights={
                                                    carDetails?.highlights
                                                }
                                                IS_RTL={IS_RTL}
                                                t={t}
                                            />
                                        ))}
                                </Box>
                                {/* Purcahse Steps Section */}
                                <Box
                                    ref={purchaseStepsSectionRef}
                                    sx={{ gridArea: 'df', marginBottom: '2vw' }}
                                >
                                    {purchaseStepsSectionInView && (
                                        <div className="md:hidden">
                                            <PurchaseCarSteps />
                                        </div>
                                    )}
                                </Box>
                                {showAppEntryPoint &&
                                    (imperfectionSectionInView ||
                                        IS_DESKTOP) && (
                                        <div className=" w-screen py-6 mb-4 en:-translate-x-[16px] ar:translate-x-[16px] px-[18px]  md:hidden border-y border-y-gray-200 ">
                                            <div
                                                className="relative h-max rounded-2xl flex gap-1"
                                                style={{
                                                    boxShadow:
                                                        '0px 0px 12px 0px rgba(0, 155, 93, 0.12)',
                                                }}
                                            >
                                                <div className="w-[45%] overflow-hidden">
                                                    <img
                                                        className="min-h-full object-cover rounded-2xl aspect-[1/1] object-top"
                                                        alt="app-entry-point"
                                                        loading="lazy"
                                                        src={myImageLoader({
                                                            src: EntryPointCarDetails.src,
                                                            width: 800,
                                                            height: 800,
                                                            quality: 100,
                                                        })}
                                                    />
                                                </div>
                                                <div className="flex flex-col w-[55%] p-3 justify-center gap-3">
                                                    <Typography
                                                        className="!text-gray-800"
                                                        variant="body1SemiBold"
                                                    >
                                                        {t('BrowseCars')}
                                                    </Typography>
                                                    <Typography
                                                        className="!text-primary-700"
                                                        variant="caption"
                                                    >
                                                        {t('ScrollSmoothly')}
                                                    </Typography>
                                                    <Button
                                                        className="!h-[34px] flex justify-center items-center"
                                                        onClick={() => {
                                                            trackEvent(
                                                                'Click On Download App',
                                                                {
                                                                    userId,
                                                                    sourceButton:
                                                                        'Car Profile',
                                                                    phone,
                                                                    email,
                                                                }
                                                            );
                                                            openApp({
                                                                universalLink:
                                                                    SINGULAR_UNI_LINK_CAR_PROFILE,
                                                            });
                                                        }}
                                                    >
                                                        <Typography variant="captionMedium">
                                                            {t('getApp')}
                                                        </Typography>
                                                        <Image
                                                            width={16}
                                                            className="mt-0.5 ar:rotate-180"
                                                            src={
                                                                ChevronRightWhite
                                                            }
                                                            alt="arrow-right"
                                                        />
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                <Box
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('Features'))
                                        ]
                                    }
                                    sx={{ ...carFeature, gridArea: 'ft' }}
                                >
                                    <div ref={featuredGridRef}>
                                        {(featuredGridInView || IS_DESKTOP) && (
                                            <GridSection
                                                gridItemVariant={
                                                    EGGRID_ITEM_VARIANT.TEXT_WITH_ICON
                                                }
                                                sectionHeader={t(
                                                    'Car Features'
                                                )}
                                                gridItems={carFeaturesObject}
                                                viewAll={
                                                    t('View All Features') || ''
                                                }
                                                gridItemClickable={true}
                                                gridItemClicked={
                                                    featureitemClicked
                                                }
                                            ></GridSection>
                                        )}
                                    </div>
                                    {shouldRenderModals && (
                                        <CarFeaturesSection
                                            carDetails={carDetails}
                                            IS_DESKTOP={IS_DESKTOP}
                                            openFeatureContainer={
                                                openFeatureContainer
                                            }
                                            carDetailsState={carDetails}
                                            setOpenFeatureContainer={
                                                setOpenFeatureContainer
                                            }
                                            featureValue={featureValue}
                                            t={t}
                                        />
                                    )}
                                </Box>

                                <Box sx={whySylndrContainer} ref={valuePropRef}>
                                    {valuePropInView && IS_DESKTOP && (
                                        <ValuePropositionSlider
                                            bannerWidth={183}
                                            bannerHeight={247}
                                        />
                                    )}
                                </Box>

                                <Box
                                    ref={
                                        tabsRef?.current[
                                            getSectionRefIndex(t('Financing'))
                                        ]
                                    }
                                    sx={{ gridArea: 'cl' }}
                                >
                                    <div>
                                        <Typography
                                            style={sectionHeader}
                                            variant={'subtitle1Bold'}
                                        >
                                            {t('Financing')}
                                        </Typography>
                                        <div className="w-full min-h-[100px]">
                                            <FinancingValuePropositions />
                                            {carInfo && carPrice ? (
                                                <FinanceCalculatorNew
                                                    carPrice={carPrice}
                                                    lenders={lenders}
                                                    otherPartners={
                                                        carDetails?.lenders
                                                    }
                                                    eventName={
                                                        'Action: Profile Financing'
                                                    }
                                                    carName={
                                                        carDetails?.sfVehicleName
                                                    }
                                                    isSold={carDetails?.isSold}
                                                    isPurchaseInProgress={
                                                        carDetails?.isPurchaseInProgress
                                                    }
                                                    carInfo={carInfo}
                                                    mobileButtons={
                                                        (
                                                            <MobileButtons
                                                                carDetails={
                                                                    carDetails
                                                                }
                                                                t={t}
                                                                carInfo={
                                                                    carInfo
                                                                }
                                                                lang={lang}
                                                            />
                                                        ) as JSX.Element
                                                    }
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        height: '200px',
                                                        background:
                                                            'transparent',
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </Box>
                            </div>
                            <Box
                                ref={infoRef}
                                sx={{ ...stickyContainer, gridArea: 'mn' }}
                            >
                                {
                                    <CarInfo
                                        IS_DESKTOP={IS_DESKTOP}
                                        carDetails={carDetails}
                                        carDetailsOverview={carDetailsOverview}
                                        inspectionRef={
                                            tabsRef?.current[
                                                getSectionRefIndex(
                                                    t('inspection')
                                                )
                                            ]
                                        }
                                        highlights={carDetails?.highlights}
                                        isRepainted={
                                            imperfectionDataGroupped?.isRepainted
                                        }
                                        numberOfRepaintedPanels={
                                            imperfectionDataGroupped?.numberOfRepaintedPanels
                                        }
                                        carInfo={carInfo}
                                    />
                                }

                                <div className="mt-6 hidden md:block">
                                    <PurchaseCarSteps />
                                </div>

                                {/* <div
                                    className={'block md:hidden direction-ltr'}
                                >
                                    <div
                                        style={{
                                            height: '110px',
                                        }}
                                    >
                                        <Carousel
                                            showDots={false}
                                            containerWidth={'250px'}
                                            containerHeight={'94px'}
                                            imageMargin="8px"
                                            imgs={IS_RTL ? bannersAR : banners}
                                            slideShow={false}
                                            dragable={true}
                                            lang={lang}
                                        />
                                    </div>
                                </div> */}
                            </Box>
                        </div>
                    </div>
                    {(carDetails?.isSold || carDetails?.isPurchaseInProgress) &&
                        similarCars && (
                            <SimilarCarsContainer
                                similarCars={similarCars}
                                opacity={opacity}
                            />
                        )}
                    <Box sx={similarCarsContainer} ref={similarCarsStaticRef}>
                        <Typography
                            style={similarCarsText}
                            variant={'subtitle2Medium'}
                        >
                            {t('Similar Cars')}
                        </Typography>

                        <div
                            ref={similarCarsSectionRef}
                            className={`${styles.similarCarsContainer} w-full`}
                        >
                            {(similarCarsSectionInView || IS_DESKTOP) &&
                                similarCars?.data
                                    ?.slice(0, 8)
                                    .map(
                                        (
                                            item: CarListingData,
                                            index: number
                                        ) => (
                                            <CarDisplayCardClient
                                                carCardLocalizations={
                                                    carCardLocalizations
                                                }
                                                carData={item}
                                                lang={lang}
                                                id={item.id}
                                                key={item.id}
                                                cardTitle={`${t(item.make)} ${
                                                    item.model
                                                } ${item.year}`}
                                                carName={item.sfVehicleName}
                                                trim={item.trim}
                                                installmentPrice={
                                                    item?.installmentPrice
                                                }
                                                totalPrice={
                                                    item.sylndrSellingPrice
                                                }
                                                kilometers={item.kilometers}
                                                year={item.year}
                                                transmission={item.transmission}
                                                images={
                                                    item?.images.length !== 0
                                                        ? item?.images
                                                        : imgs
                                                }
                                                isReserved={item.isReserved}
                                                make={item.make}
                                                model={item.model}
                                                sourceScreen={'Car Profile'}
                                                appliedFilter={false}
                                                appliedSort={false}
                                                position={index + 1}
                                                isPurchaseInProgress={
                                                    item.isPurchaseInProgress
                                                }
                                                discountPrice={
                                                    item?.discountPrice
                                                }
                                                isNewlyAdded={
                                                    item?.isNewlyAdded
                                                }
                                                tags={item.tags}
                                            />
                                        )
                                    )}
                        </div>
                    </Box>
                    <div
                        className={styles.testamonialContainer}
                        ref={videoSectionRef}
                    >
                        {(videoSectionInView || IS_DESKTOP) && (
                            <VidTestimonialsSection
                                SourceScreen={SourceScreen.CAR_PROFILE}
                                serviceName={ServiceName.RETAIL}
                            />
                        )}
                    </div>
                </div>
                <div className="md:hidden sticky bottom-0 z-[2]">
                    <MobileButtons
                        carDetails={carDetails}
                        t={t}
                        carInfo={carInfo}
                        lang={lang}
                    />
                </div>
            </CarProfileContext.Provider>
        </div>
    );
}
